// =============================================================================
// PDP STICKY ATC — reveal + scroll behaviour for the sticky Add-to-Cart pill (the floating product
// bar in sections/main-product.liquid, styled by src/scss/objects/global/_product-sticky-atc.scss).
//
// Figma: Style Guide f5iMgDjMc5dAbjAAtDfYna, node 59:3860 (annotation) / 59:3918 (desktop pill) /
// 59:32131 (mobile pill). The design specifies BEHAVIOUR the vendored bar doesn't do:
//
//   1. REVEAL  — show the pill ONLY once the PRIMARY in-page add-to-cart (the main product form) has
//                scrolled ABOVE the viewport, and retract it again near the footer. This replaces
//                Motto's vendored trigger (assets/main-product.js -> navigationInit reveals the bar
//                when the 2ND page section passes the top) — the WRONG trigger for our design.
//   2. ACTION  — the sticky "ADD TO CART" does NOT add to cart; it anchor-scrolls the page back up to
//                the primary product form so the shopper configures + adds there. (The markup is now a
//                plain `<button data-sticky-atc-scroll>`, no `<product-form>`.)
//
// We drive the reveal by TOGGLING `sticky-atc--past-primary` on the bar; the SCSS gates the slide-in
// transform on that class with `!important` so it beats Motto's INLINE transform (navigationInit still
// sets `navigation.style.transform` on scroll — `!important` overrides an inline non-important value).
//
// ANCHOR CHOICE — we observe/scroll the PERSISTENT `<product-form>` WRAPPER, not the inner submit
// button. On every variant change assets/global.js swaps the `.product-form__buttons-row` innerHTML
// (updateBuyButtonsFromHtml) and the floating `.product__media_navigation-btn` innerHTML
// (updateFloatingProductBarFromHtml), which would detach a node/listener bound to the submit button.
// The wrapper elements survive — and because the ATC sits at the bottom of the form, "the whole form
// has scrolled above the viewport" is equivalent to "past the primary ATC".
//
// Dependency-free; bundled into assets/global.autojs.js via entry/global.js -> objects/global/
// index.js. Idempotent; re-wires on shopify:section:load (the theme editor re-renders the section).
// =============================================================================

const BAR = '.product__media_navigation--atc';
const PRIMARY_FORM = '.product-form:not(.product-form--floating)'; // the in-page ATC (not the pill's)
const FOOTER = '.footer';
const PAST_PRIMARY = 'sticky-atc--past-primary';
const SCROLL_BTN = '[data-sticky-atc-scroll]';

// Wire one bar to its primary product form: reveal the pill only while the form (incl. its ATC) is
// scrolled ABOVE the viewport AND the footer isn't yet in view; the sticky button scrolls back to it.
function wire(bar, primaryForm, footer) {
  if (bar.dataset.stickyAtcInit === 'true') return; // idempotent across re-inits
  bar.dataset.stickyAtcInit = 'true';

  let pastPrimary = false; // primary buy area has scrolled up out of view
  let nearFooter = false; // footer is in view — retract so the pill doesn't overlap it

  // Shared state, updated by EITHER observer: reveal only while past the primary ATC and above the footer.
  const sync = () =>
    bar.classList.toggle(PAST_PRIMARY, pastPrimary && !nearFooter);

  if ('IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => {
      // top < 0 distinguishes "scrolled ABOVE the viewport" from "still below the fold" (both !isIntersecting).
      pastPrimary = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      sync();
    }).observe(primaryForm);

    if (footer) {
      new IntersectionObserver(([entry]) => {
        nearFooter = entry.isIntersecting;
        sync();
      }).observe(footer);
    }
  }

  // Sticky CTA = an anchor-scroll back to the primary form, NOT an add-to-cart. Delegate from the BAR
  // (it survives global.js swapping the `.product__media_navigation-btn` innerHTML on variant change),
  // and re-query the live form at click time (the cached node may have been re-rendered away).
  bar.addEventListener('click', (event) => {
    if (!event.target.closest(SCROLL_BTN)) return;
    event.preventDefault();
    const target = document.querySelector(PRIMARY_FORM);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
}

function init(scope) {
  const context = scope && scope.querySelectorAll ? scope : document;
  const bar = context.querySelector(BAR);
  if (!bar) return; // not a PDP (or the sticky bar is disabled) — nothing to wire
  const primaryForm = context.querySelector(PRIMARY_FORM);
  if (!primaryForm) return; // no in-page product form to anchor to
  // The footer lives OUTSIDE the section wrapper — always resolve it from the document.
  wire(bar, primaryForm, document.querySelector(FOOTER));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

// Theme editor re-renders the section wrapper; re-wire only within the reloaded section.
document.addEventListener('shopify:section:load', (event) =>
  init(event.target)
);

export default init;
